import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const MissingContent = () => {
  const [selectedUpdate, setSelectedUpdate] = useState<string>("all");

  const { data: updateOptions } = useQuery({
    queryKey: ["missing-content-updates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories_atp_autoteile_de')
        .select('created_at')
        .eq('cat_text', '')
        .ilike('url', '%/search/%')
        .not('name', 'eq', '')
        .eq('index', 'index, follow');
        
      if (error) throw error;
      
      const processedData = data?.map(item => {
        const date = new Date(item.created_at);
        const hour = date.getHours().toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        
        return `${hour}h ${day}-${month}-${year}`;
      });
      
      return [...new Set(processedData)];
    },
  });

  // Set the latest update as default when options are loaded
  useEffect(() => {
    if (updateOptions && updateOptions.length > 0) {
      setSelectedUpdate(updateOptions[0]);
    }
  }, [updateOptions]);

  const { data: missingContent, isLoading } = useQuery({
    queryKey: ["missing-content", selectedUpdate],
    queryFn: async () => {
      let query = supabase
        .from("categories_atp_autoteile_de")
        .select("*")
        .eq('cat_text', '')
        .ilike('url', '%/search/%')
        .not('name', 'eq', '')
        .eq('index', 'index, follow');

      if (selectedUpdate && selectedUpdate !== "all") {
        // Parse the selected update time
        const [hour, dateStr] = selectedUpdate.split('h ');
        const [day, month, year] = dateStr.split('-');
        
        // Create start and end date for the selected hour
        const startDate = new Date(
          parseInt(year),
          parseInt(month) - 1,
          parseInt(day),
          parseInt(hour)
        );
        const endDate = new Date(startDate);
        endDate.setHours(endDate.getHours() + 1);

        // Add the date filter to the query
        query = query
          .gte('created_at', startDate.toISOString())
          .lt('created_at', endDate.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;

      return data
        .map(item => ({
          ...item,
          product_count_int: parseInt(item.product_count || '0', 10)
        }))
        .sort((a, b) => b.product_count_int - a.product_count_int);
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">Loading content...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <main className="container py-6 flex-1">
        <h1 className="text-4xl font-bold mb-8 text-center">Missing Content</h1>
        
        <div className="mb-6 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Update:</span>
            <Select
              value={selectedUpdate}
              onValueChange={setSelectedUpdate}
            >
              <SelectTrigger className="w-[240px]">
                <SelectValue placeholder="Select update time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All updates</SelectItem>
                {updateOptions?.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          {missingContent?.map((item) => (
            <div 
              key={item.id} 
              className="flex items-center justify-between p-4 rounded-lg bg-muted/50"
            >
              <div className="flex-1 space-y-2">
                <div className="font-medium">
                  {item.url ? (
                    <a 
                      href={item.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      {item.name}
                    </a>
                  ) : item.name}
                </div>
                {item.title && (
                  <p className="text-sm text-muted-foreground">
                    {item.title}
                  </p>
                )}
                {item.description && (
                  <p className="text-sm text-muted-foreground">
                    {item.description}
                  </p>
                )}
              </div>
              <div className="text-sm font-medium text-muted-foreground">
                {(item.product_count_int || 0).toLocaleString()} products
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default MissingContent;